/**
 * Aludaris Recipe Builder — 2.5 fazė MVP kliento pusės logika.
 * Žr. docs/roadmap.md 2.5 fazę ir apps/web/README.md.
 *
 * Priklauso nuo window.AludarisCalcEngine (calc-engine.browser.js, sukurtas
 * `npm run build:browser` pakete packages/calc-engine — žr. apps/web/scripts/build.sh).
 *
 * DUOMENŲ MODELIS (sąmoningai paliekama vietos vandens chemijai ateityje):
 * recipe objektas žemiau turi `water: null` lauką, kurio šiuo metu niekas
 * neskaito/nerašo — kai bus pridėta vandens chemijos skaičiuoklė
 * (roadmap 2 fazės tolimesnė dalis), jis bus užpildytas be recipe objekto
 * struktūros pertvarkymo. Analogiškai `ingredients` yra raktas->masyvas
 * struktūra ("malts", "hops", "yeasts"), kad pridėti naują kategoriją
 * (pvz. "water" priedus/druskas) būtų vienos eilutės pakeitimas.
 */
(function () {
  "use strict";

  var CE = window.AludarisCalcEngine;
  var root = document.getElementById("aludaris-recipe-builder");
  if (!root || !CE) {
    if (!CE) console.error("[aludaris-recipe-builder] calc-engine.browser.js neįkeltas arba nepavyko sukurti bundle.");
    return;
  }

  var catalogEl = document.getElementById("aludaris-rb-catalog");
  var catalog = { malts: [], hops: [], yeasts: [] };
  try {
    catalog = JSON.parse(catalogEl.textContent);
  } catch (e) {
    console.error("[aludaris-recipe-builder] Nepavyko išparsinti katalogo JSON.", e);
  }

  // Shopify Liquid `product.metafields.<ns>.<key> | json` serijalizuoja metalauko
  // reikšmę pagal JOS TIKRĄ metalaukio tipą admin'e — jei metalaukis sukurtas kaip
  // TEKSTO (ne skaičiaus) tipas, "78.5" ateina kaip JSON EILUTĖ ("78.5"), ne
  // skaičius (78.5), nors liquid pusėje atrodo, kad tai skaičius. Toliau kode
  // (pvz. averageAttenuation()) naudojami griežti `typeof x === "number"`
  // patikrinimai, kurie eilutės NEATPAŽĮSTA — todėl be šios normalizacijos
  // mielių atenuacija (ir bet kuris kitas šių laukų griežtas typeof patikrinimas)
  // tyliai visada grąžindavo numatytąją reikšmę, nepriklausomai nuo pasirinkimo.
  // Normalizuojame čia, karto visiems katalogo įrašams, kad likusiam kodui
  // visada tektų tikri skaičiai (arba null, jei reikšmės nėra/tuščia).
  function normalizeNumericField(list, field) {
    (list || []).forEach(function (item) {
      var v = item[field];
      if (typeof v === "string") {
        var n = v.trim() === "" ? NaN : parseFloat(v);
        item[field] = isNaN(n) ? null : n;
      } else if (typeof v !== "number") {
        item[field] = null;
      }
    });
  }
  normalizeNumericField(catalog.malts, "extractPct");
  normalizeNumericField(catalog.malts, "colorEbc");
  normalizeNumericField(catalog.hops, "alphaAcidPct");
  normalizeNumericField(catalog.yeasts, "attenuationPct");

  // BJCP_STYLE_CATEGORIES — pilnas BJCP 2021 stilių katalogas, sugrupuotas pagal
  // kategorijas (naudojama <optgroup> generavimui, kai pažymėta "rodyti visą katalogą").
  // Šaltinis: oficialios BJCP 2021 gairės (bjcp.org/style/2021/beer/), sutikrinta su
  // brewfather.app/styles/bjcp/ pavyzdiniams stiliams. SRM reikšmės tiesiogiai iš BJCP;
  // EBC išvedama per CE.srmToEbc() rodymo metu (žr. srmToRgb()/gauge logiką žemiau).
  //
  // Kai kurios BJCP 2021 subkategorijos SĄMONINGAI praleistos, nes BJCP jas apibrėžia
  // kaip "tokios pat kaip deklaruotas bazinis stilius" arba be fiksuotų skaitinių rėžių:
  // 27A Kellerbier; 28A/28B/28C (Brett/Mixed-Fermentation/Wild Specialty); 29A/29B/29C
  // (Fruit/Fruit-Spice/Specialty Fruit); visa 30 kat. (Spiced), 31 (Alternative
  // Grain/Sugar), 32 (Smoked), 33 (Wood-Aged), 34 (Commercial/Mixed/Experimental).
  var BJCP_STYLE_CATEGORIES = [
    { name: "1. Standard American Beer", styles: [
      { code: "american-light-lager", name: "American Light Lager (1A)", og: [1.028, 1.04], ibu: [8, 12], srm: [2, 3], abv: [2.8, 4.2] },
      { code: "american-lager", name: "American Lager (1B)", og: [1.04, 1.05], ibu: [8, 18], srm: [2, 3.5], abv: [4.2, 5.3] },
      { code: "cream-ale", name: "Cream Ale (1C)", og: [1.042, 1.055], ibu: [8, 20], srm: [2, 5], abv: [4.2, 5.6] },
      { code: "american-wheat-beer", name: "American Wheat Beer (1D)", og: [1.04, 1.055], ibu: [15, 30], srm: [3, 6], abv: [4, 5.5] },
    ] },
    { name: "2. International Lager", styles: [
      { code: "international-pale-lager", name: "International Pale Lager (2A)", og: [1.042, 1.05], ibu: [18, 25], srm: [2, 6], abv: [4.5, 6] },
      { code: "international-amber-lager", name: "International Amber Lager (2B)", og: [1.042, 1.055], ibu: [8, 25], srm: [6, 14], abv: [4.5, 6] },
      { code: "international-dark-lager", name: "International Dark Lager (2C)", og: [1.044, 1.056], ibu: [8, 20], srm: [14, 30], abv: [4.2, 6] },
    ] },
    { name: "3. Czech Lager", styles: [
      { code: "czech-pale-lager", name: "Czech Pale Lager (3A)", og: [1.028, 1.044], ibu: [20, 35], srm: [3, 6], abv: [3, 4.1] },
      { code: "czech-premium-pale-lager", name: "Czech Premium Pale Lager (3B)", og: [1.044, 1.06], ibu: [30, 45], srm: [3.5, 6], abv: [4.2, 5.8] },
      { code: "czech-amber-lager", name: "Czech Amber Lager (3C)", og: [1.044, 1.06], ibu: [20, 35], srm: [10, 16], abv: [4.4, 5.8] },
      { code: "czech-dark-lager", name: "Czech Dark Lager (3D)", og: [1.044, 1.06], ibu: [18, 34], srm: [17, 35], abv: [4.4, 5.8] },
    ] },
    { name: "4. Pale Malty European Lager", styles: [
      { code: "munich-helles", name: "Munich Helles (4A)", og: [1.044, 1.048], ibu: [16, 22], srm: [3, 5], abv: [4.7, 5.4] },
      { code: "festbier", name: "Festbier (4B)", og: [1.054, 1.057], ibu: [18, 25], srm: [4, 6], abv: [5.8, 6.3] },
      { code: "helles-bock", name: "Helles Bock (4C)", og: [1.064, 1.072], ibu: [23, 35], srm: [6, 9], abv: [6.3, 7.4] },
    ] },
    { name: "5. Pale Bitter European Beer", styles: [
      { code: "german-leichtbier", name: "German Leichtbier (5A)", og: [1.026, 1.034], ibu: [15, 28], srm: [1.5, 4], abv: [2.4, 3.6] },
      { code: "kolsch", name: "Kölsch (5B)", og: [1.044, 1.05], ibu: [18, 30], srm: [3.5, 5], abv: [4.4, 5.2] },
      { code: "german-helles-exportbier", name: "German Helles Exportbier (5C)", og: [1.05, 1.058], ibu: [20, 30], srm: [4, 6], abv: [5, 6] },
      { code: "german-pils", name: "German Pils (5D)", og: [1.044, 1.05], ibu: [22, 40], srm: [2, 4], abv: [4.4, 5.2] },
    ] },
    { name: "6. Amber Malty European Lager", styles: [
      { code: "marzen", name: "Märzen (6A)", og: [1.054, 1.06], ibu: [18, 24], srm: [8, 17], abv: [5.6, 6.3] },
      { code: "rauchbier", name: "Rauchbier (6B)", og: [1.05, 1.057], ibu: [20, 30], srm: [12, 22], abv: [4.8, 6] },
      { code: "dunkles-bock", name: "Dunkles Bock (6C)", og: [1.064, 1.072], ibu: [20, 27], srm: [14, 22], abv: [6.3, 7.2] },
    ] },
    { name: "7. Amber Bitter European Beer", styles: [
      { code: "vienna-lager", name: "Vienna Lager (7A)", og: [1.048, 1.055], ibu: [18, 30], srm: [9, 15], abv: [4.7, 5.5] },
      { code: "altbier", name: "Altbier (7B)", og: [1.044, 1.052], ibu: [25, 50], srm: [9, 17], abv: [4.3, 5.5] },
    ] },
    { name: "8. Dark European Lager", styles: [
      { code: "munich-dunkel", name: "Munich Dunkel (8A)", og: [1.048, 1.056], ibu: [18, 28], srm: [17, 28], abv: [4.5, 5.6] },
      { code: "schwarzbier", name: "Schwarzbier (8B)", og: [1.046, 1.052], ibu: [20, 35], srm: [19, 30], abv: [4.4, 5.4] },
    ] },
    { name: "9. Strong European Beer", styles: [
      { code: "doppelbock", name: "Doppelbock (9A)", og: [1.072, 1.112], ibu: [16, 26], srm: [6, 25], abv: [7, 10] },
      { code: "eisbock", name: "Eisbock (9B)", og: [1.078, 1.12], ibu: [25, 35], srm: [17, 30], abv: [9, 14] },
      { code: "baltic-porter", name: "Baltic Porter (9C)", og: [1.06, 1.09], ibu: [20, 40], srm: [17, 30], abv: [5.5, 9.5] },
    ] },
    { name: "10. German Wheat Beer", styles: [
      { code: "weissbier", name: "Weissbier (10A)", og: [1.044, 1.053], ibu: [8, 15], srm: [2, 6], abv: [4.3, 5.6] },
      { code: "dunkles-weissbier", name: "Dunkles Weissbier (10B)", og: [1.044, 1.057], ibu: [10, 18], srm: [14, 23], abv: [4.3, 5.6] },
      { code: "weizenbock", name: "Weizenbock (10C)", og: [1.064, 1.09], ibu: [15, 30], srm: [6, 25], abv: [6.5, 9] },
    ] },
    { name: "11. British Bitter", styles: [
      { code: "ordinary-bitter", name: "Ordinary Bitter (11A)", og: [1.03, 1.039], ibu: [25, 35], srm: [8, 14], abv: [3.2, 3.8] },
      { code: "best-bitter", name: "Best Bitter (11B)", og: [1.04, 1.048], ibu: [25, 40], srm: [8, 16], abv: [3.8, 4.6] },
      { code: "strong-bitter", name: "Strong Bitter (11C)", og: [1.048, 1.06], ibu: [30, 50], srm: [8, 18], abv: [4.6, 6.2] },
    ] },
    { name: "12. Pale Commonwealth Beer", styles: [
      { code: "british-golden-ale", name: "British Golden Ale (12A)", og: [1.038, 1.053], ibu: [20, 45], srm: [2, 5], abv: [3.8, 5] },
      { code: "australian-sparkling-ale", name: "Australian Sparkling Ale (12B)", og: [1.038, 1.05], ibu: [20, 35], srm: [4, 7], abv: [4.5, 6] },
      { code: "english-ipa", name: "English IPA (12C)", og: [1.05, 1.07], ibu: [40, 60], srm: [6, 14], abv: [5, 7.5] },
    ] },
    { name: "13. Brown British Beer", styles: [
      { code: "dark-mild", name: "Dark Mild (13A)", og: [1.03, 1.038], ibu: [10, 25], srm: [14, 25], abv: [3, 3.8] },
      { code: "british-brown-ale", name: "British Brown Ale (13B)", og: [1.04, 1.052], ibu: [20, 30], srm: [12, 22], abv: [4.2, 5.9] },
      { code: "english-porter", name: "English Porter (13C)", og: [1.04, 1.052], ibu: [18, 35], srm: [20, 30], abv: [4, 5.4] },
    ] },
    { name: "14. Scottish Ale", styles: [
      { code: "scottish-light", name: "Scottish Light (14A)", og: [1.03, 1.035], ibu: [10, 20], srm: [17, 25], abv: [2.5, 3.3] },
      { code: "scottish-heavy", name: "Scottish Heavy (14B)", og: [1.035, 1.04], ibu: [10, 20], srm: [12, 20], abv: [3.3, 3.9] },
      { code: "scottish-export", name: "Scottish Export (14C)", og: [1.04, 1.06], ibu: [15, 30], srm: [12, 20], abv: [3.9, 6] },
    ] },
    { name: "15. Irish Beer", styles: [
      { code: "irish-red-ale", name: "Irish Red Ale (15A)", og: [1.036, 1.046], ibu: [18, 28], srm: [9, 14], abv: [3.8, 5] },
      { code: "irish-stout", name: "Irish Stout (15B)", og: [1.036, 1.044], ibu: [25, 45], srm: [25, 40], abv: [3.8, 5] },
      { code: "irish-extra-stout", name: "Irish Extra Stout (15C)", og: [1.052, 1.062], ibu: [35, 50], srm: [30, 40], abv: [5, 6.5] },
    ] },
    { name: "16. Dark British Beer", styles: [
      { code: "sweet-stout", name: "Sweet Stout (16A)", og: [1.044, 1.06], ibu: [20, 40], srm: [30, 40], abv: [4, 6] },
      { code: "oatmeal-stout", name: "Oatmeal Stout (16B)", og: [1.045, 1.065], ibu: [25, 40], srm: [22, 40], abv: [4.2, 5.9] },
      { code: "tropical-stout", name: "Tropical Stout (16C)", og: [1.056, 1.075], ibu: [30, 50], srm: [30, 40], abv: [5.5, 8] },
      { code: "foreign-extra-stout", name: "Foreign Extra Stout (16D)", og: [1.056, 1.075], ibu: [50, 70], srm: [30, 40], abv: [6.3, 8] },
    ] },
    { name: "17. British Strong Ale", styles: [
      { code: "british-strong-ale", name: "British Strong Ale (17A)", og: [1.055, 1.08], ibu: [30, 60], srm: [8, 22], abv: [5.5, 8] },
      { code: "old-ale", name: "Old Ale (17B)", og: [1.055, 1.088], ibu: [30, 60], srm: [10, 22], abv: [5.5, 9] },
      { code: "wee-heavy", name: "Wee Heavy (17C)", og: [1.07, 1.13], ibu: [17, 35], srm: [14, 25], abv: [6.5, 10] },
      { code: "english-barleywine", name: "English Barleywine (17D)", og: [1.08, 1.12], ibu: [35, 70], srm: [8, 22], abv: [8, 12] },
    ] },
    { name: "18. American Pale Ale", styles: [
      { code: "blonde-ale", name: "Blonde Ale (18A)", og: [1.038, 1.054], ibu: [15, 28], srm: [3, 6], abv: [3.8, 5.5] },
      { code: "american-pale-ale", name: "American Pale Ale (18B)", og: [1.045, 1.06], ibu: [30, 50], srm: [5, 10], abv: [4.5, 6.2] },
    ] },
    { name: "19. Amber and Brown American Beer", styles: [
      { code: "american-amber-ale", name: "American Amber Ale (19A)", og: [1.045, 1.06], ibu: [25, 40], srm: [10, 17], abv: [4.5, 6.2] },
      { code: "california-common", name: "California Common (19B)", og: [1.048, 1.054], ibu: [30, 45], srm: [9, 14], abv: [4.5, 5.5] },
      { code: "american-brown-ale", name: "American Brown Ale (19C)", og: [1.045, 1.06], ibu: [20, 30], srm: [18, 35], abv: [4.3, 6.2] },
    ] },
    { name: "20. American Porter and Stout", styles: [
      { code: "american-porter", name: "American Porter (20A)", og: [1.05, 1.07], ibu: [25, 50], srm: [22, 40], abv: [4.8, 6.5] },
      { code: "american-stout", name: "American Stout (20B)", og: [1.05, 1.075], ibu: [35, 75], srm: [30, 40], abv: [5, 7] },
      { code: "imperial-stout", name: "Imperial Stout (20C)", og: [1.075, 1.115], ibu: [50, 90], srm: [30, 40], abv: [8, 12] },
    ] },
    { name: "21. IPA", styles: [
      { code: "american-ipa", name: "American IPA (21A)", og: [1.056, 1.07], ibu: [40, 70], srm: [6, 14], abv: [5.5, 7.5] },
      { code: "belgian-ipa", name: "Belgian IPA (21B)", og: [1.058, 1.08], ibu: [50, 100], srm: [5, 8], abv: [6.2, 9.5] },
      { code: "black-ipa", name: "Black IPA (21B)", og: [1.05, 1.085], ibu: [50, 90], srm: [25, 40], abv: [5.5, 9] },
      { code: "brown-ipa", name: "Brown IPA (21B)", og: [1.056, 1.07], ibu: [40, 70], srm: [18, 35], abv: [5.5, 7.5] },
      { code: "brut-ipa", name: "Brut IPA (21B)", og: [1.046, 1.057], ibu: [20, 30], srm: [2, 4], abv: [6, 7.5] },
      { code: "red-ipa", name: "Red IPA (21B)", og: [1.056, 1.07], ibu: [40, 70], srm: [11, 17], abv: [5.5, 7.5] },
      { code: "rye-ipa", name: "Rye IPA (21B)", og: [1.056, 1.075], ibu: [50, 75], srm: [6, 14], abv: [5.5, 8] },
      { code: "white-ipa", name: "White IPA (21B)", og: [1.056, 1.065], ibu: [40, 70], srm: [5, 6], abv: [5.5, 7] },
      { code: "hazy-ipa", name: "Hazy IPA (21C)", og: [1.06, 1.085], ibu: [25, 60], srm: [3, 7], abv: [6, 9] },
    ] },
    { name: "22. Strong American Ale", styles: [
      { code: "double-ipa", name: "Double IPA (22A)", og: [1.065, 1.085], ibu: [60, 100], srm: [6, 14], abv: [7.5, 10] },
      { code: "american-strong-ale", name: "American Strong Ale (22B)", og: [1.062, 1.09], ibu: [50, 100], srm: [7, 18], abv: [6.3, 10] },
      { code: "american-barleywine", name: "American Barleywine (22C)", og: [1.08, 1.12], ibu: [50, 100], srm: [9, 18], abv: [8, 12] },
      { code: "wheatwine", name: "Wheatwine (22D)", og: [1.08, 1.12], ibu: [30, 60], srm: [6, 14], abv: [8, 12] },
    ] },
    { name: "23. European Sour Ale", styles: [
      { code: "berliner-weisse", name: "Berliner Weisse (23A)", og: [1.028, 1.032], ibu: [3, 8], srm: [2, 3], abv: [2.8, 3.8] },
      { code: "flanders-red-ale", name: "Flanders Red Ale (23B)", og: [1.048, 1.057], ibu: [10, 25], srm: [10, 17], abv: [4.6, 6.5] },
      { code: "oud-bruin", name: "Oud Bruin (23C)", og: [1.04, 1.074], ibu: [20, 25], srm: [17, 22], abv: [4, 8] },
      { code: "lambic", name: "Lambic (23D)", og: [1.04, 1.054], ibu: [0, 10], srm: [3, 6], abv: [5, 6.5] },
      { code: "gueuze", name: "Gueuze (23E)", og: [1.04, 1.054], ibu: [0, 10], srm: [5, 6], abv: [5, 8] },
      { code: "fruit-lambic", name: "Fruit Lambic (23F)", og: [1.04, 1.06], ibu: [0, 10], srm: [3, 7], abv: [5, 7] },
      { code: "gose", name: "Gose (23G)", og: [1.036, 1.056], ibu: [5, 12], srm: [3, 4], abv: [4.2, 4.8] },
    ] },
    { name: "24. Belgian Ale", styles: [
      { code: "witbier", name: "Witbier (24A)", og: [1.044, 1.052], ibu: [8, 20], srm: [2, 4], abv: [4.5, 5.5] },
      { code: "belgian-pale-ale", name: "Belgian Pale Ale (24B)", og: [1.048, 1.054], ibu: [20, 30], srm: [8, 14], abv: [4.8, 5.5] },
      { code: "biere-de-garde", name: "Bière de Garde (24C)", og: [1.06, 1.08], ibu: [18, 28], srm: [6, 19], abv: [6, 8.5] },
    ] },
    { name: "25. Strong Belgian Ale", styles: [
      { code: "belgian-blond-ale", name: "Belgian Blond Ale (25A)", og: [1.062, 1.075], ibu: [15, 30], srm: [4, 6], abv: [6, 7.5] },
      { code: "saison", name: "Saison (25B)", og: [1.048, 1.065], ibu: [20, 35], srm: [5, 22], abv: [3.5, 9.5] },
      { code: "belgian-golden-strong-ale", name: "Belgian Golden Strong Ale (25C)", og: [1.07, 1.095], ibu: [22, 35], srm: [3, 6], abv: [7.5, 10.5] },
    ] },
    { name: "26. Trappist Ale", styles: [
      { code: "belgian-single", name: "Belgian Single (26A)", og: [1.044, 1.054], ibu: [25, 45], srm: [3, 5], abv: [4.8, 6] },
      { code: "belgian-dubbel", name: "Belgian Dubbel (26B)", og: [1.062, 1.075], ibu: [15, 25], srm: [10, 17], abv: [6, 7.6] },
      { code: "belgian-tripel", name: "Belgian Tripel (26C)", og: [1.075, 1.085], ibu: [20, 40], srm: [4.5, 7], abv: [7.5, 9.5] },
      { code: "belgian-dark-strong-ale", name: "Belgian Dark Strong Ale (26D)", og: [1.075, 1.11], ibu: [20, 35], srm: [12, 22], abv: [8, 12] },
    ] },
    { name: "27. Historical Beer", styles: [
      { code: "kentucky-common", name: "Kentucky Common (27A)", og: [1.044, 1.055], ibu: [15, 30], srm: [11, 20], abv: [4, 5.5] },
      { code: "lichtenhainer", name: "Lichtenhainer (27A)", og: [1.032, 1.04], ibu: [5, 12], srm: [3, 6], abv: [3.5, 4.7] },
      { code: "london-brown-ale", name: "London Brown Ale (27A)", og: [1.033, 1.038], ibu: [15, 20], srm: [22, 35], abv: [2.8, 3.6] },
      { code: "piwo-grodziskie", name: "Piwo Grodziskie (27A)", og: [1.028, 1.032], ibu: [20, 35], srm: [3, 6], abv: [2.5, 3.3] },
      { code: "pre-prohibition-lager", name: "Pre-Prohibition Lager (27A)", og: [1.044, 1.06], ibu: [25, 40], srm: [3, 6], abv: [4.5, 6] },
      { code: "pre-prohibition-porter", name: "Pre-Prohibition Porter (27A)", og: [1.046, 1.06], ibu: [20, 30], srm: [20, 30], abv: [4.5, 6] },
      { code: "roggenbier", name: "Roggenbier (27A)", og: [1.046, 1.056], ibu: [10, 20], srm: [14, 19], abv: [4.5, 6] },
      { code: "sahti", name: "Sahti (27A)", og: [1.076, 1.12], ibu: [0, 15], srm: [4, 22], abv: [7, 11] },
    ] },
    { name: "28. American Wild Ale", styles: [
      { code: "straight-sour-beer", name: "Straight Sour Beer (28D)", og: [1.048, 1.065], ibu: [3, 8], srm: [2, 3], abv: [4.5, 7] },
    ] },
    { name: "29. Fruit Beer", styles: [
      { code: "grape-ale", name: "Grape Ale (29D)", og: [1.059, 1.075], ibu: [10, 30], srm: [4, 8], abv: [6, 8.5] },
    ] },
  ];

  // BEER_STYLES_ALL — visi BJCP_STYLE_CATEGORIES stiliai vienu plokščiu masyvu
  // (naudojama getSelectedStyle() paieškai, nepriklausomai nuo to, ar rodomas
  // pilnas katalogas, ar bazinis rinkinys).
  var BEER_STYLES_ALL = BJCP_STYLE_CATEGORIES.reduce(function (acc, cat) {
    return acc.concat(cat.styles);
  }, []);

  // BEER_STYLES_BASIC — supaprastintas, NE-BJCP "bazinis" rinkinys (numatytasis
  // pasirinkimas, kol vartotojas nepažymi "rodyti visą BJCP katalogą"). Rėžiai
  // sąmoningai platūs (apytiksliai, ne tiksli BJCP subkategorija) — skirti bendram
  // orientavimuisi pradedantiesiems, o ne tikslioms varžyboms.
  var BEER_STYLES_BASIC = [
    { code: "basic-light-ale", name: "Šviesus alus", og: [1.038, 1.056], ibu: [15, 35], srm: [3, 8], abv: [4.0, 5.8] },
    { code: "basic-dark-ale", name: "Tamsus alus", og: [1.045, 1.065], ibu: [20, 40], srm: [20, 40], abv: [4.5, 7.0] },
    { code: "basic-lager", name: "Lageris", og: [1.044, 1.056], ibu: [15, 30], srm: [3, 15], abv: [4.2, 5.6] },
  ];

  // BEER_STYLES — sujungtas sąrašas (bazinis + visas BJCP katalogas) paieškai pagal
  // kodą, nepriklausomai nuo to, kuris rinkinys šiuo metu rodomas <select> lauke.
  var BEER_STYLES = BEER_STYLES_BASIC.concat(BEER_STYLES_ALL);

  // populateStyleSelect(showAll) — perkuria #rb-style <select> turinį: arba 3
  // bazinius įrašus (showAll=false, numatytoji būsena), arba visą BJCP katalogą,
  // sugrupuotą į <optgroup> pagal kategoriją (showAll=true, rodoma tik pažymėjus
  // varnelę "Rodyti visą BJCP katalogą"). Jei anksčiau pasirinktas stilius
  // neegzistuoja naujai atvaizduotame sąraše (pvz. perjungus iš pilno katalogo
  // į bazinį rinkinį), pasirinkimas grąžinamas į "— Nepasirinkta —".
  function populateStyleSelect(showAll) {
    if (!els.styleSelect) return;
    var previousCode = recipe.styleCode || "";
    var visibleCodes = {};
    els.styleSelect.innerHTML = "";

    var placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "— Nepasirinkta —";
    els.styleSelect.appendChild(placeholder);

    function addOption(container, style) {
      var opt = document.createElement("option");
      opt.value = style.code;
      opt.textContent = style.name;
      container.appendChild(opt);
      visibleCodes[style.code] = true;
    }

    if (showAll) {
      BJCP_STYLE_CATEGORIES.forEach(function (cat) {
        var group = document.createElement("optgroup");
        group.label = cat.name;
        cat.styles.forEach(function (style) { addOption(group, style); });
        els.styleSelect.appendChild(group);
      });
    } else {
      BEER_STYLES_BASIC.forEach(function (style) { addOption(els.styleSelect, style); });
    }

    if (previousCode && visibleCodes[previousCode]) {
      els.styleSelect.value = previousCode;
    } else {
      els.styleSelect.value = "";
      recipe.styleCode = "";
    }
  }

  // ---- Įrangos profiliai (vandens/salinimo skaičiavimui) --------------------
  // "confirmed: true" — skaičiai paimti tiesiogiai iš gamintojo dokumentacijos.
  // "confirmed: false" — įverčiai (bendruomenės šaltiniai arba ekstrapoliacija
  // iš giminingo modelio), NEPATVIRTINTI gamintojo — vartotojui rodoma pastaba
  // (žr. renderWaterPanel()). Šaltiniai/pagrindimas — žr. PROGRESS.md Round 6
  // įrašą. "base" (bazė) profilis neturi vandens skaičiavimo laukų — pasirinkus
  // jį, rodomos tik fiksuotos salinimo temperatūros (67°C/78°C mashout),
  // o vandens kiekį vartotojas parenka pats (žr. renderWaterPanel()).
  var EQUIPMENT_PROFILES = [
    { code: "base", name: "Bazė (tik temperatūros, be vandens skaičiavimo)", base: true },
    {
      code: "brewzilla-35", name: "BrewZilla 35L (Gen 4)",
      boilOffRateLPerHour: 2.0, trubChillerLossL: 1.0, mashTunDeadspaceL: 0.5,
      confirmed: false,
    },
    {
      code: "brewzilla-65", name: "BrewZilla 65L (Gen 4)",
      boilOffRateLPerHour: 3.0, trubChillerLossL: 1.5, mashTunDeadspaceL: 0.75,
      confirmed: false,
    },
    {
      code: "brewster-beacon-40", name: "Brewster Beacon 40L",
      boilOffRateLPerHour: 1.5, trubChillerLossL: 1.5, mashTunDeadspaceL: 1.0,
      confirmed: false,
    },
    {
      code: "brewmonk-b40", name: "Brew Monk B40",
      boilOffRateLPerHour: 2.0, trubChillerLossL: 1.0, mashTunDeadspaceL: 0.5,
      confirmed: false,
    },
    {
      code: "brewmonk-b50", name: "Brew Monk B50",
      boilOffRateLPerHour: 2.5, trubChillerLossL: 1.25, mashTunDeadspaceL: 0.6,
      confirmed: false,
    },
    {
      code: "brewmonk-b70", name: "Brew Monk B70",
      boilOffRateLPerHour: 3.0, trubChillerLossL: 1.5, mashTunDeadspaceL: 0.75,
      confirmed: false,
    },
    {
      code: "grainfather-g30", name: "Grainfather G30 / G30³",
      boilOffRateLPerHour: 3.0, trubChillerLossL: 2.0, mashTunDeadspaceL: 3.5,
      grainAbsorptionLPerKg: 0.8,
      confirmed: true,
    },
    {
      code: "grainfather-g70", name: "Grainfather G70",
      boilOffRateLPerHour: 4.5, trubChillerLossL: 2.5, mashTunDeadspaceL: 5.0,
      grainAbsorptionLPerKg: 0.8,
      confirmed: false,
    },
    {
      code: "simple-pot-30", name: "Paprastas puodas (30L, BIAB)",
      boilOffRateLPerHour: 3.5, trubChillerLossL: 1.0, mashTunDeadspaceL: 0.5,
      confirmed: false,
    },
  ];

  // SRM->RGB aproksimacija (plačiai naudojama atviro kodo formulė, žr. brauhaus.js
  // Brauhaus.srmToRgb) — naudojama taurės skysčio spalvai nustatyti.
  function srmToRgb(srm) {
    var r = Math.round(Math.min(255, Math.max(0, 255 * Math.pow(0.975, srm))));
    var g = Math.round(Math.min(255, Math.max(0, 245 * Math.pow(0.88, srm))));
    var b = Math.round(Math.min(255, Math.max(0, 220 * Math.pow(0.7, srm))));
    return "rgb(" + r + "," + g + "," + b + ")";
  }

  // ---- Recipe state --------------------------------------------------
  var recipe = {
    name: "",
    batchVolumeL: 20,
    boilTimeMin: 60,
    efficiencyPct: 72,
    styleCode: "",
    showAllStyles: false,
    equipmentProfile: "base",
    ingredients: {
      malts: [],   // { productId, variantId, title, weightG, colorEbc, extractPct }
      hops: [],    // { productId, variantId, title, weightG, alphaAcidPct, boilTimeMin }
      yeasts: [],  // { productId, variantId, title, quantity, attenuationPct }
    },
    water: null,   // rezervuota ateičiai (vandens chemija) — žr. failo komentarą viršuje
    milling: false,
  };

  var STORAGE_KEY = "aludaris_recipe_builder_v1";

  function loadFromStorage() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var saved = JSON.parse(raw);
      if (saved && typeof saved === "object") {
        recipe.name = typeof saved.name === "string" ? saved.name : recipe.name;
        recipe.batchVolumeL = saved.batchVolumeL || recipe.batchVolumeL;
        recipe.boilTimeMin = typeof saved.boilTimeMin === "number" ? saved.boilTimeMin : recipe.boilTimeMin;
        recipe.efficiencyPct = saved.efficiencyPct || recipe.efficiencyPct;
        recipe.styleCode = typeof saved.styleCode === "string" ? saved.styleCode : recipe.styleCode;
        recipe.showAllStyles = typeof saved.showAllStyles === "boolean" ? saved.showAllStyles : recipe.showAllStyles;
        recipe.equipmentProfile = typeof saved.equipmentProfile === "string" ? saved.equipmentProfile : recipe.equipmentProfile;
        recipe.ingredients.malts = Array.isArray(saved.ingredients && saved.ingredients.malts) ? saved.ingredients.malts : [];
        recipe.ingredients.hops = Array.isArray(saved.ingredients && saved.ingredients.hops) ? saved.ingredients.hops : [];
        recipe.ingredients.yeasts = Array.isArray(saved.ingredients && saved.ingredients.yeasts) ? saved.ingredients.yeasts : [];
        recipe.milling = !!saved.milling;
      }
    } catch (e) {
      // Blogi/pasenę duomenys localStorage — ignoruojame ir pradedame nuo tuščio recepto.
      console.warn("[aludaris-recipe-builder] Nepavyko atkurti recepto iš localStorage.", e);
    }
  }

  function saveToStorage() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(recipe));
    } catch (e) {
      // localStorage gali būti nepasiekiamas (privatus naršymas ir pan.) — receptas
      // tiesiog neišsisaugos tarp apsilankymų, bet puslapis toliau veikia.
    }
  }

  // ---- DOM refs --------------------------------------------------------
  var els = {
    recipeName: document.getElementById("rb-recipe-name"),
    batchVolume: document.getElementById("rb-batch-volume"),
    boilTime: document.getElementById("rb-boil-time"),
    efficiency: document.getElementById("rb-efficiency"),
    styleSelect: document.getElementById("rb-style"),
    styleShowAll: document.getElementById("rb-style-show-all"),
    equipmentSelect: document.getElementById("rb-equipment"),
    waterContent: root.querySelector("[data-water-content]"),
    milling: document.getElementById("rb-milling"),
    recipeBody: root.querySelector("[data-recipe-body]"),
    addToCartBtn: root.querySelector("[data-add-to-cart]"),
    message: root.querySelector("[data-rb-message]"),
    glassLiquid: root.querySelector("[data-glass-liquid]"),
    gaugesWrap: root.querySelector("[data-gauges]"),
    gaugesStyleName: root.querySelector("[data-style-name]"),
    gaugesEmpty: root.querySelector("[data-gauges-empty]"),
    gaugesRows: root.querySelector("[data-gauges-rows]"),
    stats: {
      og: root.querySelector('[data-stat="og"]'),
      fg: root.querySelector('[data-stat="fg"]'),
      abv: root.querySelector('[data-stat="abv"]'),
      ibu: root.querySelector('[data-stat="ibu"]'),
      ebc: root.querySelector('[data-stat="ebc"]'),
      price: root.querySelector('[data-stat="price"]'),
    },
  };

  els.gaugeRows = {};
  ["og", "ibu", "ebc", "abv"].forEach(function (key) {
    var rowEl = root.querySelector('.rb-gauge-row[data-gauge="' + key + '"]');
    if (!rowEl) return;
    els.gaugeRows[key] = {
      row: rowEl,
      value: rowEl.querySelector("[data-gauge-value]"),
      zone: rowEl.querySelector("[data-gauge-zone]"),
      marker: rowEl.querySelector("[data-gauge-marker]"),
      range: rowEl.querySelector("[data-gauge-range]"),
    };
  });

  var millingVariantId = root.dataset.millingVariantId ? Number(root.dataset.millingVariantId) : null;
  var millingKgPerUnit = Number(root.dataset.millingKgPerUnit || 20);
  var millingPrice = root.dataset.millingPrice ? Number(root.dataset.millingPrice) : null;
  var pdfVariantId = root.dataset.pdfVariantId ? Number(root.dataset.pdfVariantId) : null;

  // Paskutiniai apskaičiuoti rodikliai — naudojami sudarant kompaktišką recepto
  // santrauką (_recipe_json), kuri keliauja su "Recepto PDF" prekės eilute į
  // užsakymą (žr. buildCartItems() žemiau). Atnaujinama kiekvieną kartą
  // recalcAndRender() metu.
  var lastStats = { og: null, fg: null, abv: null, ibu: null, ebc: null };

  // ---- Catalog rendering -------------------------------------------
  function renderCatalogList(category) {
    var container = root.querySelector('[data-catalog-list="' + category + '"]');
    if (!container) return;
    var searchInput = root.querySelector('.rb-catalog-search[data-category="' + category + '"]');
    var query = (searchInput && searchInput.value || "").trim().toLowerCase();
    var items = catalog[category] || [];
    var filtered = query
      ? items.filter(function (p) { return p.title.toLowerCase().indexOf(query) !== -1; })
      : items;

    // MVP: rodome ne daugiau kaip 40 vienu metu (su paieška susiaurinama toliau).
    var shown = filtered.slice(0, 40);

    container.innerHTML = "";
    if (shown.length === 0) {
      var empty = document.createElement("div");
      empty.className = "rb-catalog-item";
      empty.innerHTML = '<span class="rb-item-name">Nieko nerasta.</span>';
      container.appendChild(empty);
      return;
    }

    shown.forEach(function (product) {
      var hasAvailableVariant = (product.variants || []).some(function (v) { return v.available; });
      var row = document.createElement("div");
      row.className = "rb-catalog-item";

      var metaText = "";
      if (category === "malts") {
        metaText = [
          product.colorEbc != null ? product.colorEbc + " EBC" : null,
          product.extractPct != null ? product.extractPct + "% ekstr." : null,
        ].filter(Boolean).join(" · ");
      } else if (category === "hops") {
        metaText = product.alphaAcidPct != null ? product.alphaAcidPct + "% AA" : "";
      } else if (category === "yeasts") {
        metaText = product.attenuationPct != null ? product.attenuationPct + "% atenuacija" : "";
      }

      row.innerHTML =
        '<span class="rb-item-name">' + escapeHtml(product.title) + "</span>" +
        '<span class="rb-item-meta">' + escapeHtml(metaText) + "</span>" +
        '<button type="button" class="rb-add-btn"' + (hasAvailableVariant ? "" : " disabled") + ">+</button>";

      row.querySelector(".rb-add-btn").addEventListener("click", function () {
        addIngredient(category, product);
      });
      container.appendChild(row);
    });
  }

  function escapeHtml(str) {
    return String(str == null ? "" : str).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // ---- Adding / editing ingredients ----------------------------------
  function pickDefaultVariant(product) {
    var available = (product.variants || []).filter(function (v) { return v.available; });
    var pool = available.length ? available : (product.variants || []);
    // Numatytoji: mažiausia pakuotė (patogiau tiksliau apskaičiuoti kiekį).
    var withGrams = pool.filter(function (v) { return typeof v.grams === "number" && v.grams > 0; });
    if (withGrams.length) {
      return withGrams.reduce(function (a, b) { return b.grams < a.grams ? b : a; });
    }
    return pool[0] || null;
  }

  function addIngredient(category, product) {
    var variant = pickDefaultVariant(product);
    if (!variant) return;

    if (category === "malts") {
      var maltEntry = {
        productId: product.id, variantId: variant.id, title: product.title,
        weightG: 500, colorEbc: product.colorEbc, extractPct: product.extractPct,
        availableVariants: product.variants,
      };
      recipe.ingredients.malts.push(maltEntry);
    } else if (category === "hops") {
      var hopEntry = {
        productId: product.id, variantId: variant.id, title: product.title,
        weightG: 20, alphaAcidPct: product.alphaAcidPct, boilTimeMin: 60,
        availableVariants: product.variants,
      };
      recipe.ingredients.hops.push(hopEntry);
    } else if (category === "yeasts") {
      recipe.ingredients.yeasts.push({
        productId: product.id, variantId: variant.id, title: product.title,
        quantity: 1, attenuationPct: product.attenuationPct,
        availableVariants: product.variants,
      });
    }
    saveAndRerender();
  }

  function removeIngredient(category, index) {
    recipe.ingredients[category].splice(index, 1);
    saveAndRerender();
  }

  // ---- Package-size rounding (recommend a variant+qty for a target weight) ---
  function suggestPackaging(availableVariants, targetGrams) {
    var withGrams = (availableVariants || []).filter(function (v) { return typeof v.grams === "number" && v.grams > 0 && v.available; });
    if (!withGrams.length || !targetGrams) return null;
    // Sudedame kelias skirtingas pakuotes (pvz. 1000g + 100g), kad tiksliai
    // padengtume reikiamą kiekį: didžiausios pakuotės imamos kiek telpa be
    // likučio, o mažiausia (paskutinė) visada suapvalinama į viršų, kad
    // likutis būtų pilnai padengtas. Pvz. 1700g su [100g, 1000g] pakuotėmis
    // -> 1×1000g + 7×100g.
    var sorted = withGrams.slice().sort(function (a, b) { return b.grams - a.grams; });
    var remaining = targetGrams;
    var lines = [];
    sorted.forEach(function (v, i) {
      var isLast = i === sorted.length - 1;
      var qty = isLast ? (remaining > 0 ? Math.ceil(remaining / v.grams) : 0) : Math.floor(remaining / v.grams);
      if (qty > 0) {
        lines.push({ variant: v, quantity: qty });
        remaining -= qty * v.grams;
      }
    });
    if (!lines.length) return null;
    var totalGrams = lines.reduce(function (s, l) { return s + l.variant.grams * l.quantity; }, 0);
    return { lines: lines, totalGrams: totalGrams };
  }

  // ---- Shared helpers --------------------------------------------------
  function totalMaltGrams() {
    return recipe.ingredients.malts.reduce(function (s, m) { return s + (m.weightG || 0); }, 0);
  }

  // ---- Recipe table rendering ----------------------------------------
  // Pakuotės (Shopify prekės pakuotės, pvz. 100g/500g/1kg) skiriasi nuo
  // recepto tikslinio kiekio: vartotojas gali įrašyti BET KOKĮ gramų skaičių
  // (pvz. 150g salyklo, 18g apynių) — tai yra jo RECEPTO reikšmė, naudojama
  // OG/IBU/ABV ir kt. skaičiavimuose. "Pakuotė" stulpelis parodo, kiek
  // realiai reikės UŽSISAKYTI, visada apvalinant Į VIRŠŲ per suggestPackaging()
  // (pvz. 150g -> 200g/2×100g, 18g -> 25g, 33g -> 50g arba 2×25g — priklausomai
  // nuo to, kokios pakuotės parduotuvėje realiai egzistuoja tam produktui).
  // Ta pati suggestPackaging() naudojama ir čia rodymui, ir buildCartItems()
  // realiam krepšelio kiekiui — abu visada sutampa.
  function packageCellHtml(ing) {
    var suggestion = suggestPackaging(ing.availableVariants, ing.weightG);
    return suggestion
      ? suggestion.lines.map(function (l) { return escapeHtml(l.variant.title) + " × " + l.quantity; }).join(", ")
      : '<span class="rb-hint rb-hint-warn">nerasta pakuotės duomenų — nebus pridėta į krepšelį</span>';
  }

  // Kai vartotojas keičia recepto kiekį (gramus), atnaujiname TIK išvestinius
  // laukelius (pakuotę, % salyklų) tiesiogiai per DOM — NEPERKURIAME viso
  // <tr>/<input> elemento (t.y. NEKVIEČIAME renderRecipeTable() čia). Kitaip
  // kiekvienas klavišo paspaudimas iš naujo sukurtų patį input'ą, atimdamas
  // fokusą/kursoriaus poziciją per pačio rašymo vidurį — dėl to anksčiau
  // buvo neįmanoma laisvai įrašyti norimo skaičiaus ranka (tekdavo naudotis
  // tik rodyklėmis).
  function updateWeightDerivedCells(changedTr, changedIng) {
    var pkgCell = changedTr.children[4];
    if (pkgCell) pkgCell.innerHTML = packageCellHtml(changedIng);

    if (recipe.ingredients.malts.indexOf(changedIng) !== -1) {
      var totalMaltG = totalMaltGrams();
      var maltRows = els.recipeBody.querySelectorAll('tr[data-cat="malts"]');
      Array.prototype.forEach.call(maltRows, function (row) {
        var idx = parseInt(row.getAttribute("data-idx"), 10);
        var m = recipe.ingredients.malts[idx];
        var cell = row.children[2];
        if (cell && m) cell.textContent = totalMaltG > 0 ? ((m.weightG / totalMaltG) * 100).toFixed(1) + "%" : "—";
      });
    }
  }

  function renderRecipeTable() {
    var body = els.recipeBody;
    body.innerHTML = "";
    var all = []
      .concat(recipe.ingredients.malts.map(function (i, idx) { return { cat: "malts", idx: idx, item: i }; }))
      .concat(recipe.ingredients.hops.map(function (i, idx) { return { cat: "hops", idx: idx, item: i }; }))
      .concat(recipe.ingredients.yeasts.map(function (i, idx) { return { cat: "yeasts", idx: idx, item: i }; }));

    if (all.length === 0) {
      body.innerHTML = '<tr class="rb-empty-row"><td colspan="6">Kol kas nė vieno ingrediento nepridėta.</td></tr>';
      return;
    }

    var totalMaltG = totalMaltGrams();

    all.forEach(function (entry) {
      var tr = document.createElement("tr");
      tr.setAttribute("data-cat", entry.cat);
      tr.setAttribute("data-idx", String(entry.idx));
      var qtyCell, pkgCell, pctCell;

      if (entry.cat === "yeasts") {
        var y = entry.item;
        qtyCell = '<input type="number" min="1" step="1" value="' + y.quantity + '" data-qty-input>';
        pkgCell = "pakelis";
        tr.innerHTML =
          "<td>" + escapeHtml(y.title) + "</td><td>" + qtyCell + "</td><td>—</td><td>—</td><td>" + pkgCell + '</td><td><button type="button" class="rb-remove-btn" data-remove>&times;</button></td>';
        tr.querySelector("[data-qty-input]").addEventListener("input", function (e) {
          y.quantity = Math.max(1, parseInt(e.target.value, 10) || 1);
          saveAndRerenderStatsOnly();
        });
      } else {
        var ing = entry.item;
        var weightField = "weightG";
        pkgCell = packageCellHtml(ing);
        // min="0"/step="1" — laisvas įrašymas rankomis: recepto kiekis NEBŪTINAI
        // sutampa su pakuotės dydžiu (žr. komentarą aukščiau), tad neribojame
        // įvedimo lauko pagal turimas pakuotes.
        qtyCell = '<input type="number" min="0" step="1" value="' + ing[weightField] + '" data-weight-input> g';
        pctCell = entry.cat === "malts" && totalMaltG > 0
          ? ((ing.weightG / totalMaltG) * 100).toFixed(1) + "%"
          : "—";
        var boilCell = entry.cat === "hops"
          ? '<input type="number" min="0" step="5" value="' + ing.boilTimeMin + '" data-boil-input> min'
          : "—";
        tr.innerHTML =
          "<td>" + escapeHtml(ing.title) + "</td><td>" + qtyCell + "</td><td>" + pctCell + "</td><td>" + boilCell + "</td><td>" + pkgCell + '</td><td><button type="button" class="rb-remove-btn" data-remove>&times;</button></td>';
        tr.querySelector("[data-weight-input]").addEventListener("input", function (e) {
          var typed = Math.max(0, parseFloat(e.target.value) || 0);
          ing[weightField] = typed;
          updateWeightDerivedCells(tr, ing);
          recalcAndRender();
          saveToStorage();
        });
        if (entry.cat === "hops") {
          tr.querySelector("[data-boil-input]").addEventListener("input", function (e) {
            ing.boilTimeMin = Math.max(0, parseFloat(e.target.value) || 0);
            recalcAndRender();
            saveToStorage();
          });
        }
      }

      tr.querySelector("[data-remove]").addEventListener("click", function () {
        removeIngredient(entry.cat, entry.idx);
      });
      body.appendChild(tr);
    });
  }

  // ---- Calculations (calc-engine) -------------------------------------
  function toCalcFermentables() {
    return recipe.ingredients.malts.map(function (m) {
      var potentialSg = m.extractPct != null ? CE.yieldPctToPotentialSg(m.extractPct) : 1.037;
      var colorLovibond = m.colorEbc != null ? CE.ebcToSrm(m.colorEbc) : undefined; // žr. failo viršuje esantį komentarą apie aproksimaciją
      return { name: m.title, weightKg: m.weightG / 1000, potentialSg: potentialSg, colorLovibond: colorLovibond };
    });
  }

  function toCalcHops() {
    return recipe.ingredients.hops.map(function (h) {
      return { name: h.title, weightG: h.weightG, alphaAcidPct: h.alphaAcidPct || 0, boilTimeMin: h.boilTimeMin };
    });
  }

  function getSelectedStyle() {
    if (!recipe.styleCode) return null;
    for (var i = 0; i < BEER_STYLES.length; i++) {
      if (BEER_STYLES[i].code === recipe.styleCode) return BEER_STYLES[i];
    }
    return null;
  }

  function getSelectedEquipmentProfile() {
    for (var i = 0; i < EQUIPMENT_PROFILES.length; i++) {
      if (EQUIPMENT_PROFILES[i].code === recipe.equipmentProfile) return EQUIPMENT_PROFILES[i];
    }
    return EQUIPMENT_PROFILES[0]; // "base"
  }

  // Fiksuotos salinimo temperatūros — vartotojo nurodymu VISADA vienpakopis
  // salinimas 67°C + salinimo pabaiga (mash out) 78°C, be jokio skaičiavimo
  // pagal receptą (žr. PROGRESS.md Round 5 įrašą). Naudojama ir čia (gyvame
  // skaičiuoklyje), ir generate-recipe-pdf.js (Recepto PDF).
  var MASH_TEMP_C = 67;
  var MASH_OUT_TEMP_C = 78;
  var WATER_TO_GRAIN_RATIO_L_PER_KG = 3.0;
  var GRAIN_TEMP_C = 20;
  var MASH_OUT_INFUSION_WATER_TEMP_C = 96;

  // Apskaičiuoja ir atvaizduoja "Salinimas ir vanduo" bloką (žr.
  // recipe-builder.liquid data-water-content). Pasirinkus "bazę" — tik
  // fiksuotos temperatūros, be vandens kiekio (vartotojas renkasi pats,
  // svarbu tik, kad galutinis kiekis = partijos dydis). Pasirinkus konkrečią
  // įrangą — pilnas vandens skaičiavimas (calc-engine), naudojant tos
  // įrangos išgaravimo/nuostolių rodiklius.
  function renderWaterPanel() {
    if (!els.waterContent) return;
    var profile = getSelectedEquipmentProfile();
    var grainWeightKg = totalMaltGrams() / 1000;
    var batchVolumeL = recipe.batchVolumeL;

    var rows = [];
    rows.push(["Salinimas", MASH_TEMP_C + "°C"]);
    rows.push(["Salinimo pabaiga (mash out)", MASH_OUT_TEMP_C + "°C"]);

    if (profile.base) {
      els.waterContent.innerHTML =
        renderWaterRows(rows) +
        '<p class="rb-hint">Partijos dydis: ' + fmtGauge(batchVolumeL, 1) + ' L. Vandens kiekį pasirinkite patys pagal savo įrangą/efektyvumą — svarbu tik, kad GALUTINIS (į raugą nubėgęs) kiekis būtų lygus partijos dydžiui.</p>';
      return;
    }

    var mashWaterL = null, strikeTempC = null, mashOutInfusionL = null, water = null, spargeVolumeL = null;
    try {
      if (grainWeightKg > 0 && batchVolumeL > 0) {
        mashWaterL = grainWeightKg * WATER_TO_GRAIN_RATIO_L_PER_KG;
        strikeTempC = CE.calculateStrikeWaterTemp({
          targetMashTempC: MASH_TEMP_C, grainTempC: GRAIN_TEMP_C, waterToGrainRatioLPerKg: WATER_TO_GRAIN_RATIO_L_PER_KG,
        });
        mashOutInfusionL = CE.calculateMashInfusionVolume({
          currentMashTempC: MASH_TEMP_C, targetMashTempC: MASH_OUT_TEMP_C, grainWeightKg: grainWeightKg,
          currentMashWaterL: mashWaterL, infusionWaterTempC: MASH_OUT_INFUSION_WATER_TEMP_C,
        });
        water = CE.calculateTotalWaterNeeded({
          batchVolumeL: batchVolumeL, boilTimeMin: recipe.boilTimeMin,
          boilOffRateLPerHour: profile.boilOffRateLPerHour, trubChillerLossL: profile.trubChillerLossL,
          mashTunDeadspaceL: profile.mashTunDeadspaceL, totalGrainWeightKg: grainWeightKg,
          grainAbsorptionLPerKg: profile.grainAbsorptionLPerKg,
        });
        spargeVolumeL = Math.max(0, water.totalWaterNeededL - mashWaterL - mashOutInfusionL);
      }
    } catch (e) {
      console.warn("[aludaris-recipe-builder] Vandens skaičiavimo klaida", e);
    }

    if (!water) {
      els.waterContent.innerHTML =
        renderWaterRows(rows) +
        '<p class="rb-hint">Pridėkite salyklo, kad pamatytumėte apskaičiuotą vandens kiekį pagal "' + escapeHtml(profile.name) + '".</p>';
      return;
    }

    rows.push(["Strike vanduo (užpylimui)", fmtGauge(mashWaterL, 1) + " L, ~" + fmtGauge(strikeTempC, 0) + "°C"]);
    rows.push(["Papildymas salinimo pabaigai", fmtGauge(mashOutInfusionL, 1) + " L (~" + MASH_OUT_INFUSION_WATER_TEMP_C + "°C)"]);
    rows.push(["Priedėtinis (sparge) vanduo", fmtGauge(spargeVolumeL, 1) + " L"]);
    rows.push(["Vandens iš viso", fmtGauge(water.totalWaterNeededL, 1) + " L"]);
    rows.push(["Apytikslis virimo tūris", fmtGauge(water.preBoilVolumeL, 1) + " L"]);

    var html = renderWaterRows(rows);
    if (profile.confirmed === false) {
      html += '<p class="rb-hint rb-water-assumption">Šie skaičiai — įverčiai pagal viešai prieinamą informaciją, NEPATVIRTINTI gamintojo. Jei žinote tikslius savo įrangos rodiklius (išgaravimas L/val, deadspace), praneškite mums — atnaujinsime.</p>';
    }
    els.waterContent.innerHTML = html;
  }

  function renderWaterRows(rows) {
    return '<div class="rb-water-rows">' + rows.map(function (r) {
      return '<div class="rb-water-row"><span class="rb-water-label">' + escapeHtml(r[0]) + '</span><span class="rb-water-value">' + r[1] + '</span></div>';
    }).join("") + "</div>";
  }

  function fmtGauge(value, decimals) {
    return value == null || isNaN(value) ? "—" : value.toFixed(decimals);
  }

  // Piešia vieną "gauge" eilutę: raudona-žalia-raudona juosta su rodykle ties
  // dabartine recepto reikšme. Vizualus intervalas praplečiamas 40% už stiliaus
  // rėžių abiejose pusėse, kad rodyklė turėtų kur atsidurti, jei receptas
  // neatitinka stiliaus.
  function renderGaugeRow(rowRefs, value, range, unit, decimals) {
    if (!rowRefs) return;
    if (!range) {
      rowRefs.value.textContent = "—";
      rowRefs.range.textContent = "";
      rowRefs.zone.style.width = "0%";
      rowRefs.marker.style.display = "none";
      return;
    }
    var min = range[0], max = range[1];
    var span = max - min;
    var pad = span > 0 ? span * 0.4 : Math.max(Math.abs(min) * 0.15, 1);
    var vmin = min - pad, vmax = max + pad;
    var totalSpan = vmax - vmin || 1;
    function pct(v) { return Math.min(100, Math.max(0, ((v - vmin) / totalSpan) * 100)); }
    rowRefs.zone.style.left = pct(min).toFixed(1) + "%";
    rowRefs.zone.style.width = Math.max(0, pct(max) - pct(min)).toFixed(1) + "%";
    rowRefs.range.textContent = fmtGauge(min, decimals) + "–" + fmtGauge(max, decimals) + " " + unit;
    if (value == null || isNaN(value)) {
      rowRefs.value.textContent = "—";
      rowRefs.marker.style.display = "none";
      rowRefs.row.classList.remove("rb-gauge-out-of-range");
      return;
    }
    rowRefs.marker.style.display = "block";
    rowRefs.marker.style.left = pct(value).toFixed(1) + "%";
    rowRefs.value.textContent = fmtGauge(value, decimals) + " " + unit;
    rowRefs.row.classList.toggle("rb-gauge-out-of-range", value < min || value > max);
  }

  function recalcAndRender() {
    var fermentables = toCalcFermentables();
    var hops = toCalcHops();
    var batchVolumeL = recipe.batchVolumeL;
    var efficiencyPct = recipe.efficiencyPct;

    var og = null, fg = null, abv = null, ibu = null, srm = null;
    try {
      if (fermentables.length && batchVolumeL > 0) {
        og = CE.calculateOG({ fermentables: fermentables, batchVolumeL: batchVolumeL, efficiencyPct: efficiencyPct });
        var avgAttenuation = averageAttenuation();
        fg = CE.estimateFG(og, avgAttenuation);
        abv = CE.calculateABV(og, fg);
        srm = CE.calculateSRM({ fermentables: fermentables, batchVolumeL: batchVolumeL });
      }
      if (hops.length && batchVolumeL > 0) {
        ibu = CE.calculateIbu({ hops: hops, batchVolumeL: batchVolumeL, boilGravity: og || 1.05 }, "tinseth");
      }
    } catch (e) {
      console.warn("[aludaris-recipe-builder] Skaičiavimo klaida", e);
    }

    var ebc = srm != null ? CE.srmToEbc(srm) : null;

    els.stats.og.textContent = og ? og.toFixed(3) : "—";
    els.stats.fg.textContent = fg ? fg.toFixed(3) : "—";
    els.stats.abv.textContent = abv != null ? abv.toFixed(1) + "%" : "—";
    els.stats.ibu.textContent = ibu != null ? ibu.toFixed(0) : "—";
    els.stats.ebc.textContent = ebc != null ? ebc.toFixed(1) : "—";
    els.stats.price.textContent = estimateTotalPrice().toFixed(2);

    lastStats.og = og;
    lastStats.fg = fg;
    lastStats.abv = abv;
    lastStats.ibu = ibu;
    lastStats.ebc = ebc;

    if (els.glassLiquid) {
      els.glassLiquid.setAttribute("fill", srm != null ? srmToRgb(srm) : "#e4ddc8");
    }

    var selectedStyle = getSelectedStyle();
    if (els.gaugesWrap) {
      if (selectedStyle) {
        if (els.gaugesEmpty) els.gaugesEmpty.style.display = "none";
        if (els.gaugesRows) els.gaugesRows.style.display = "";
        if (els.gaugesStyleName) els.gaugesStyleName.textContent = selectedStyle.name;
        renderGaugeRow(els.gaugeRows.og, og, selectedStyle.og, "SG", 3);
        renderGaugeRow(els.gaugeRows.ibu, ibu, selectedStyle.ibu, "IBU", 0);
        renderGaugeRow(
          els.gaugeRows.ebc,
          ebc,
          selectedStyle.srm ? [CE.srmToEbc(selectedStyle.srm[0]), CE.srmToEbc(selectedStyle.srm[1])] : null,
          "EBC",
          1
        );
        renderGaugeRow(els.gaugeRows.abv, abv, selectedStyle.abv, "%", 1);
      } else {
        if (els.gaugesEmpty) els.gaugesEmpty.style.display = "";
        if (els.gaugesRows) els.gaugesRows.style.display = "none";
        if (els.gaugesStyleName) els.gaugesStyleName.textContent = "—";
      }
    }

    els.addToCartBtn.disabled = !(fermentables.length || hops.length || recipe.ingredients.yeasts.length);

    renderWaterPanel();
  }

  function averageAttenuation() {
    var yeasts = recipe.ingredients.yeasts.filter(function (y) { return typeof y.attenuationPct === "number"; });
    if (!yeasts.length) return 75; // MVP numatytoji, kai mielių atenuacija nežinoma/nepasirinkta.
    var sum = yeasts.reduce(function (s, y) { return s + y.attenuationPct; }, 0);
    return sum / yeasts.length;
  }

  function estimateTotalPrice() {
    var total = 0;
    recipe.ingredients.malts.concat(recipe.ingredients.hops).forEach(function (ing) {
      var suggestion = suggestPackaging(ing.availableVariants, ing.weightG);
      if (suggestion) suggestion.lines.forEach(function (l) { total += l.variant.price * l.quantity; });
    });
    recipe.ingredients.yeasts.forEach(function (y) {
      var v = (y.availableVariants || []).find(function (vv) { return vv.id === y.variantId; }) || (y.availableVariants || [])[0];
      if (v) total += v.price * y.quantity;
    });
    if (recipe.milling && millingPrice != null) {
      var maltKg = recipe.ingredients.malts.reduce(function (s, m) { return s + m.weightG / 1000; }, 0);
      total += millingPrice * Math.max(1, Math.ceil(maltKg / millingKgPerUnit));
    }
    return total;
  }

  function saveAndRerender() {
    renderRecipeTable();
    recalcAndRender();
    saveToStorage();
  }
  function saveAndRerenderStatsOnly() {
    recalcAndRender();
    saveToStorage();
  }

  // ---- Cart integration -------------------------------------------
  function buildCartItems() {
    var items = [];
    var skipped = [];
    var recipeName = (recipe.name || "").trim() || "Nepavadintas receptas";
    recipe.ingredients.malts.forEach(function (m) {
      var s = suggestPackaging(m.availableVariants, m.weightG);
      if (s) {
        s.lines.forEach(function (l) {
          items.push({ id: l.variant.id, quantity: l.quantity, properties: { "Receptas": recipeName, "Ingredientas": "Salyklas (" + m.weightG + "g reikalinga)" } });
        });
      } else {
        skipped.push(m.title);
      }
    });
    recipe.ingredients.hops.forEach(function (h) {
      var s = suggestPackaging(h.availableVariants, h.weightG);
      if (s) {
        s.lines.forEach(function (l) {
          items.push({ id: l.variant.id, quantity: l.quantity, properties: { "Receptas": recipeName, "Ingredientas": "Apyniai, " + h.boilTimeMin + " min virimo" } });
        });
      } else {
        skipped.push(h.title);
      }
    });
    recipe.ingredients.yeasts.forEach(function (y) {
      items.push({ id: y.variantId, quantity: y.quantity, properties: { "Receptas": recipeName, "Ingredientas": "Mielės" } });
    });
    if (recipe.milling && millingVariantId) {
      var maltKg = recipe.ingredients.malts.reduce(function (s, m) { return s + m.weightG / 1000; }, 0);
      var units = Math.max(1, Math.ceil(maltKg / millingKgPerUnit));
      items.push({ id: millingVariantId, quantity: units, properties: { "Receptas": recipeName, "Ingredientas": "Salyklo traiškymas" } });
    }
    // Nemokama "Recepto PDF" eilutė — pridedama tik jei receptas turi bent
    // vieną tikrą ingrediento eilutę (kad "bent vienas ingredientas"
    // patikrinimas addRecipeToCart() nebūtų apeinamas vien šia eilute).
    // Paslėpta "_recipe_json" savybė (raktas su "_" priešdėliu) nerodoma
    // pirkėjui krepšelyje/užsakyme, bet lieka pasiekiama per Admin API/webhooks
    // — būtent tai naudos būsimas backend'as PDF generavimui po apmokėjimo.
    if (items.length > 0 && pdfVariantId) {
      items.push({
        id: pdfVariantId,
        quantity: 1,
        properties: {
          "Receptas": recipeName,
          "Ingredientas": "Recepto PDF (atsiunčiama el. paštu po apmokėjimo)",
          "_recipe_json": JSON.stringify(buildRecipeSummary(recipeName)),
        },
      });
    }
    return { items: items, skipped: skipped };
  }

  // Kompaktiška recepto santrauka, keliaujanti su "Recepto PDF" prekės eilute
  // (žr. buildCartItems() aukščiau) — būsimas backend'as ją panaudos PDF
  // generavimui, reaguodamas į Shopify "orders/paid" webhook'ą.
  function buildRecipeSummary(recipeName) {
    var style = getSelectedStyle();
    return {
      name: recipeName,
      style: style ? style.name : null,
      batchVolumeL: recipe.batchVolumeL,
      boilTimeMin: recipe.boilTimeMin,
      efficiencyPct: recipe.efficiencyPct,
      equipmentProfile: recipe.equipmentProfile,
      og: lastStats.og,
      fg: lastStats.fg,
      abv: lastStats.abv,
      ibu: lastStats.ibu,
      ebc: lastStats.ebc,
      malts: recipe.ingredients.malts.map(function (m) {
        return { title: m.title, weightG: m.weightG, colorEbc: m.colorEbc, extractPct: m.extractPct };
      }),
      hops: recipe.ingredients.hops.map(function (h) {
        return { title: h.title, weightG: h.weightG, alphaAcidPct: h.alphaAcidPct, boilTimeMin: h.boilTimeMin };
      }),
      yeasts: recipe.ingredients.yeasts.map(function (y) {
        return { title: y.title, quantity: y.quantity, attenuationPct: y.attenuationPct };
      }),
    };
  }

  function showMessage(text, kind) {
    els.message.innerHTML = '<div class="rb-message rb-message-' + kind + '">' + escapeHtml(text) + "</div>";
  }

  // Po sekmingo /cart/add.js, Kalles (T4S) tema SAVO PACIOS "I krepseli"
  // mygtukams atnaujina antrastes krepselio skaiciu/suma ir atidaro
  // mini-krepselio (drawer) stalciu savo vidine JS logika, kurios musu
  // (atskiro, keliu prekiu vienu metu pridedancio) fetch() iskvietimas
  // NEPALIECIA - todel be sios funkcijos vartotojas iki siol matydavo
  // pasikeitima tik perkrove puslapi. Cia PERNAUDOJAMA ta pati Shopify
  // Section Rendering AJAX API (?section_id=...), kuria tema naudoja savo
  // "cart_data" (trumpas skaicius/suma tekstas, atskirtas [t4s_split1]/
  // [t4s_split2] zymemis) ir "mini_cart" (visas stalciaus HTML) sekcijoms -
  // patikrinta tiesiogiai narsykleje, kad abu endpoint'ai veikia bet kuriame
  // puslapyje, ne tik produkto. Jei temos struktura ateityje pasikeis
  // (pvz. #t4s-mini_cart elemento nebeliks), funkcija tyliai nieko
  // nedaro - pati /cart/add.js operacija vis tiek jau pavyko nepriklausomai.
  function refreshThemeCartUI() {
    return Promise.all([
      fetch("/?section_id=cart_data").then(function (r) { return r.text(); }),
      fetch("/?section_id=mini_cart").then(function (r) { return r.text(); }),
    ])
      .then(function (results) {
        var cartDataHtml = results[0];
        var miniCartHtml = results[1];

        var m = cartDataHtml.match(/\[t4s_split1\]([\s\S]*?)\[t4s_split1\]/);
        if (m) {
          var parts = m[1].split("[t4s_split2]");
          var countText = parts[0];
          var priceText = parts[1];
          document.querySelectorAll("[data-cart-count]").forEach(function (el) {
            el.textContent = countText;
          });
          if (priceText != null) {
            document.querySelectorAll("[data-cart-tt-price]").forEach(function (el) {
              el.textContent = priceText;
            });
          }
        }

        var wrapper = document.getElementById("t4s-mini_cart");
        var overlay = document.querySelector(".t4s-close-overlay");
        function closeAludarisDrawer() {
          if (wrapper) wrapper.setAttribute("aria-hidden", "true");
          if (overlay) overlay.classList.remove("is--visible");
        }
        if (wrapper) {
          var tmp = document.createElement("div");
          tmp.innerHTML = miniCartHtml;
          var inner = tmp.firstElementChild;
          // Pakeitus turinį, senas temos "X" uždarymo mygtukas (su savo
          // pridėtu click klausytoju) dingsta kartu su senu HTML — naujas
          // mygtukas, kurį ką tik įterpėme, tokio klausytojo neturi, todėl
          // pridedame savo, kad "X" ir toliau uždarytų stalčių.
          if (inner) wrapper.innerHTML = inner.innerHTML;
          wrapper.querySelectorAll("[data-drawer-close]").forEach(function (btn) {
            btn.addEventListener("click", closeAludarisDrawer);
          });
          wrapper.setAttribute("aria-hidden", "false");
        }
        if (overlay) {
          overlay.classList.add("is--visible");
          if (!overlay.__aludarisCloseBound) {
            overlay.__aludarisCloseBound = true;
            overlay.addEventListener("click", function () {
              if (wrapper && wrapper.getAttribute("aria-hidden") === "false") closeAludarisDrawer();
            });
          }
        }
      })
      .catch(function () {
        // Krepselio UI atnaujinimas nepavyko (pvz. temos struktura pasikeite) -
        // ignoruojama, nes pats /cart/add.js jau pavyko pries tai.
      });
  }

  function addRecipeToCart() {
    if (!recipe.name || !recipe.name.trim()) {
      showMessage("Įveskite recepto pavadinimą prieš pridedant į krepšelį — tai padės atskirti, kuriam receptui priklauso šie ingredientai.", "warn");
      if (els.recipeName) els.recipeName.focus();
      return;
    }
    var built = buildCartItems();
    var items = built.items;
    var skipped = built.skipped;
    if (!items.length) {
      showMessage("Pridėkite bent vieną ingredientą prieš pridedant į krepšelį.", "warn");
      return;
    }
    els.addToCartBtn.disabled = true;
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw new Error(err.description || "Klaida pridedant į krepšelį."); });
        return res.json();
      })
      .then(function () {
        if (skipped.length) {
          showMessage("Receptas pridėtas į krepšelį, bet šie ingredientai neturi pakuotės duomenų ir NEBUVO pridėti: " + skipped.join(", ") + ".", "warn");
        } else {
          showMessage("Receptas pridėtas į krepšelį!", "success");
        }
        refreshThemeCartUI();
      })
      .catch(function (err) {
        showMessage(err.message || "Nepavyko pridėti į krepšelį.", "error");
      })
      .finally(function () {
        els.addToCartBtn.disabled = false;
      });
  }

  // ---- Wire up events -------------------------------------------------
  loadFromStorage();

  if (els.recipeName) els.recipeName.value = recipe.name || "";
  els.batchVolume.value = recipe.batchVolumeL;
  els.boilTime.value = recipe.boilTimeMin;
  els.efficiency.value = recipe.efficiencyPct;
  if (els.styleShowAll) els.styleShowAll.checked = !!recipe.showAllStyles;
  populateStyleSelect(recipe.showAllStyles);
  if (els.equipmentSelect) els.equipmentSelect.value = recipe.equipmentProfile || "base";
  if (els.milling) els.milling.checked = recipe.milling;

  if (els.recipeName) {
    els.recipeName.addEventListener("input", function (e) {
      recipe.name = e.target.value;
      saveToStorage();
    });
  }
  els.batchVolume.addEventListener("input", function (e) { recipe.batchVolumeL = parseFloat(e.target.value) || 0; recalcAndRender(); saveToStorage(); });
  els.boilTime.addEventListener("input", function (e) { recipe.boilTimeMin = parseFloat(e.target.value) || 0; recalcAndRender(); saveToStorage(); });
  els.efficiency.addEventListener("input", function (e) { recipe.efficiencyPct = parseFloat(e.target.value) || 0; recalcAndRender(); saveToStorage(); });
  if (els.styleSelect) {
    els.styleSelect.addEventListener("change", function (e) {
      recipe.styleCode = e.target.value;
      recalcAndRender();
      saveToStorage();
    });
  }
  if (els.styleShowAll) {
    els.styleShowAll.addEventListener("change", function (e) {
      recipe.showAllStyles = !!e.target.checked;
      populateStyleSelect(recipe.showAllStyles);
      recalcAndRender();
      saveToStorage();
    });
  }
  if (els.milling) els.milling.addEventListener("change", function (e) { recipe.milling = e.target.checked; recalcAndRender(); saveToStorage(); });
  if (els.equipmentSelect) {
    els.equipmentSelect.addEventListener("change", function (e) {
      recipe.equipmentProfile = e.target.value;
      recalcAndRender();
      saveToStorage();
    });
  }

  root.querySelectorAll(".rb-catalog-search").forEach(function (input) {
    input.addEventListener("input", function () { renderCatalogList(input.dataset.category); });
  });

  els.addToCartBtn.addEventListener("click", addRecipeToCart);

  renderCatalogList("malts");
  renderCatalogList("hops");
  renderCatalogList("yeasts");
  renderRecipeTable();
  recalcAndRender();
})();
